import React, { useState, useEffect } from 'react';
import { Platform, View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useBiometricSignIn } from '../biometric/useBiometricSignIn';
import { AuthLayout } from '../components/AuthLayout';
import { LoginForm } from '../components/forms/LoginForm';
import Loading from '../components/Loading';
import { TwoFactorForm } from '../components/forms/TwoFactorForm';
import { ROUTES } from '../utils/constants';
import { completeAuth, attemptSessionRestore, submitZendeskHandoff } from '../services/authService';
import { resolveDestination } from '../utils/resolveDestination';
import { useLoginRouter } from '../hooks/useLoginRouter';
import { useOnAuthenticated } from '../hooks/useOnAuthenticated';

interface LoginCredentials {
  email: string;
  password: string;
}

export default function LoginScreen() {
  const router = useLoginRouter();
  const params = useLocalSearchParams();
  const [checking, setChecking] = useState(true);
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [credentials, setCredentials] = useState<LoginCredentials>({ email: '', password: '' });
  const [lastFourDigits, setLastFourDigits] = useState('');
  const onAuthenticated = useOnAuthenticated();

  const nextUrl = typeof params.next === 'string' ? params.next : undefined;
  const returnTo = typeof params.return_to === 'string' ? params.return_to : undefined;

  // Auto-triggers biometric on mount when enrolled; falls back to LoginForm.
  const biometric = useBiometricSignIn({
    onSuccess: async (token) => {
      await completeAuth({
        token,
        method: 'biometric',
        urlNextUrl: nextUrl,
        urlReturnTo: returnTo,
        onAuthenticated,
      });
    },
  });

  useEffect(() => {
    if (Platform.OS !== 'web') {
      setChecking(false);
      return;
    }
    if (window.location.hash === '#sso-login') {
      const base = window.location.pathname.replace(/\/+$/, '');
      const forwarded = new URLSearchParams();
      if (nextUrl) forwarded.set('next', nextUrl);
      if (returnTo) forwarded.set('return_to', returnTo);
      const qs = forwarded.toString() ? `?${forwarded}` : '';
      window.location.replace(`${base}/sso-login${qs}`);
      return;
    }
    resolveDestination('member_app')
      .then((fallback) =>
        attemptSessionRestore(fallback, { urlNextUrl: nextUrl, urlReturnTo: returnTo })
      )
      .then((result) => {
        if (result.status === 'redirect') {
          window.location.href = result.url;
        } else if (result.status === 'zendesk') {
          submitZendeskHandoff(result.jwt, result.returnTo);
        } else {
          setChecking(false);
        }
      });
  }, []);

  const handleSSOClick = () => {
    // Both params must survive: a Zendesk bounce carries only ?return_to, and
    // sso-login needs it to offer the employee Okta option.
    const ssoParams: Record<string, string> = {};
    if (nextUrl) ssoParams.next = nextUrl;
    if (returnTo) ssoParams.return_to = returnTo;
    router.push(
      Object.keys(ssoParams).length ? { pathname: ROUTES.SSO, params: ssoParams } : ROUTES.SSO
    );
  };

  const handleTwoFactorRequired = (email: string, password: string, fourDigits: string) => {
    setCredentials({ email, password });
    setLastFourDigits(fourDigits);
    setShowTwoFactor(true);
  };

  const handleBackToLogin = () => {
    setCredentials({ email: '', password: '' });
    setShowTwoFactor(false);
  };

  if (checking) return null;

  // Native phones open the login screen on the carousel, unless a step is already in progress.
  const mobileEntry = !showTwoFactor && params.sign_in_error !== 'true';

  // Keep showing the loading state while biometric eligibility is being
  // computed (or the biometric prompt is on screen) so enrolled users don't
  // flash the password form before Face ID appears.
  if (biometric.checking || biometric.inFlight) {
    return (
      <AuthLayout mobileEntry={mobileEntry}>
        <View className='w-full items-center justify-center py-16'>
          <Loading />
        </View>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout mobileEntry={mobileEntry} onDismiss={showTwoFactor ? handleBackToLogin : undefined}>
      {!showTwoFactor ? (
        <LoginForm
          onSSOClick={handleSSOClick}
          onTwoFactorRequired={handleTwoFactorRequired}
          biometricEligible={biometric.eligible}
          biometricType={biometric.biometricType}
          onBiometricRetry={biometric.attempt}
          defaultEmail={biometric.prefillEmail}
        />
      ) : (
        <TwoFactorForm
          onBack={handleBackToLogin}
          email={credentials.email}
          password={credentials.password}
          lastFourDigits={lastFourDigits}
          nextUrl={nextUrl}
          returnTo={returnTo}
        />
      )}
    </AuthLayout>
  );
}
